import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@ngneat/transloco';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { AuthService } from 'src/app/service/auth.service';
import { GlobalService } from 'src/app/service/global.service';
import { AccountService } from 'src/app/service/account.service';
import { TokenPackage } from 'src/app/model/response/token-package.model';

@Component({
  selector: 'app-token-purchase-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, ProgressSpinnerModule, TranslocoRootModule],
  templateUrl: './token-purchase-modal.component.html',
  styleUrl: './token-purchase-modal.component.css',
})
export class TokenPurchaseModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() currentCreditsTotal: number | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  packages: TokenPackage[] = [];
  loading: boolean = false;
  buyingPackageId: string | null = null;
  purchaseError: string = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private globalService: GlobalService,
    private accountService: AccountService,
    private translocoService: TranslocoService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && this.packages.length === 0) {
      this.loadPackages();
    }
    if (changes['visible']?.currentValue === false) {
      this.resetState();
    }
  }

  /**
   * Mesma regra de coerência tela/cobrança do pro-feature-modal: só passa a moeda da assinatura
   * quando ela está ATIVA (é quando o backend cobra nela); do contrário a tela cai no GeoIP,
   * igual ao backend trataria uma nova assinatura.
   */
  private loadPackages(): void {
    this.loading = true;
    const userId = this.authService.getUserInformations();
    this.accountService.getAccount(userId).subscribe({
      next: (account) => {
        const currency = account?.planInformation?.status === 'active'
          ? account.planInformation.currency ?? undefined
          : undefined;
        this.fetchPackages(currency);
      },
      // getAccount falhando não pode travar a vitrine: segue sem override (GeoIP), como hoje.
      error: () => this.fetchPackages(undefined),
    });
  }

  private fetchPackages(currency: string | undefined): void {
    this.globalService.getTokenPackages(currency).subscribe({
      next: (packages) => {
        // Pacotes sem preço configurado na moeda resolvida não são ofertáveis.
        this.packages = packages.filter(p => p.price != null);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private resetState(): void {
    this.purchaseError = '';
    this.buyingPackageId = null;
  }

  formatPrice(pkg: TokenPackage): string {
    const locale = pkg.currency === 'USD' ? 'en-US' : 'pt-BR';
    return (pkg.price as number).toLocaleString(locale, { style: 'currency', currency: pkg.currency });
  }

  buyPackage(pkg: TokenPackage): void {
    this.purchaseError = '';
    this.buyingPackageId = pkg.packageId;
    const userId = this.authService.getUserInformations();

    sessionStorage.setItem('tokenPurchaseReturnUrl', this.router.url);
    const expected = (this.currentCreditsTotal ?? 0) + pkg.tokenAmount;
    sessionStorage.setItem('tokenPurchaseExpectedCredits', String(expected));

    this.accountService.checkoutTokenPackage(userId, pkg.packageId).subscribe({
      next: (response) => {
        window.location.href = response.checkoutUrl;
      },
      error: (err: HttpErrorResponse) => {
        this.buyingPackageId = null;
        sessionStorage.removeItem('tokenPurchaseReturnUrl');
        sessionStorage.removeItem('tokenPurchaseExpectedCredits');
        if (err.status === 409) {
          this.purchaseError = this.translocoService.translate('tokenPurchase.errors.noActiveSubscription');
        } else if (err.status === 404) {
          this.purchaseError = this.translocoService.translate('tokenPurchase.errors.packageNotFound');
          this.packages = [];
          this.loadPackages();
        } else if (err.status === 400) {
          this.purchaseError = this.translocoService.translate('tokenPurchase.errors.priceNotConfigured');
        } else {
          this.purchaseError = this.translocoService.translate('tokenPurchase.errors.checkoutFailed');
        }
      },
    });
  }

  close(): void {
    this.visible = false;
    this.resetState();
    this.visibleChange.emit(false);
  }
}
